const API_BASE = window.location.origin + '/api';

// ==========================================
// Admin Auth (token storage + fetch helpers)
// ==========================================
const ADMIN_TOKEN_KEY = 'hvc_admin_token';

export const adminAuth = {
  getToken: () => {
    try { return localStorage.getItem(ADMIN_TOKEN_KEY) || ''; }
    catch { return ''; }
  },
  setToken: (token) => {
    try { localStorage.setItem(ADMIN_TOKEN_KEY, token); } catch {}
  },
  clear: () => {
    try { localStorage.removeItem(ADMIN_TOKEN_KEY); } catch {}
  },
  isAuthenticated: () => !!adminAuth.getToken(),

  login: async (username, password) => {
    const r = await fetch(`${API_BASE}/admin/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password }),
    });
    const data = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(data?.error || 'Falha no login');
    adminAuth.setToken(data.token);
    return data;
  },

  me: async () => {
    const r = await adminFetch(`${API_BASE}/admin/auth/me`);
    if (!r.ok) throw new Error('Sessao invalida');
    return r.json();
  },

  changePassword: async (currentPassword, newPassword) => {
    const r = await adminFetch(`${API_BASE}/admin/auth/change-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ currentPassword, newPassword }),
    });
    const data = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(data?.error || 'Falha ao trocar senha');
    return data;
  },

  logout: () => {
    adminAuth.clear();
    if (typeof window !== 'undefined') window.location.href = '/admin/login';
  },
};

/** fetch wrapper that attaches admin bearer token and redirects to /admin/login on 401. */
async function adminFetch(url, options = {}) {
  const token = adminAuth.getToken();
  const headers = { ...(options.headers || {}) };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(url, { ...options, headers });
  if (res.status === 401) {
    adminAuth.clear();
    if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/admin/login')) {
      window.location.href = '/admin/login';
    }
  }
  return res;
}
/** Parse response body as JSON; if the body is HTML (e.g. a Cloudflare 522
 *  error page) or the status is non-2xx, throw instead of returning garbage. */
const adminJson = async (url, options) => {
  const res = await adminFetch(url, options);
  const ct = res.headers.get('content-type') || '';
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`HTTP ${res.status} ${url}: ${text.slice(0, 200)}`);
  }
  if (!ct.includes('application/json')) {
    const text = await res.text().catch(() => '');
    throw new Error(`Resposta nao-JSON de ${url}: ${text.slice(0, 200)}`);
  }
  return res.json();
};

// ==========================================
// API Publica (sem autenticacao)
// ==========================================
export const publicApi = {
  getRoomInfo: (roomId) =>
    fetch(`${API_BASE}/public/room/${roomId}`).then(r => r.json()),

  joinRoom: (roomId, displayName, role = 'participant') =>
    fetch(`${API_BASE}/public/join/${roomId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ displayName, role }),
    }).then(r => r.json()),

  // Chat endpoints now require the chatToken issued at join time. Pass it as the
  // 2nd argument; every call forwards it as Authorization: Bearer <token>.
  getMessages: (roomId, chatToken, limit = 100) =>
    fetch(`${API_BASE}/rooms/${roomId}/chat?limit=${limit}`, {
      headers: chatToken ? { Authorization: `Bearer ${chatToken}` } : {},
    }).then(r => r.json()),

  sendMessage: (roomId, chatToken, senderName, senderRole, content, type = 'text', extras = {}) =>
    fetch(`${API_BASE}/rooms/${roomId}/chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(chatToken ? { Authorization: `Bearer ${chatToken}` } : {}),
      },
      body: JSON.stringify({
        senderName, senderRole, content, type,
        fileUrl: extras.fileUrl, fileName: extras.fileName,
      }),
    }).then(r => r.json()),

  endRoom: (roomId, chatToken) =>
    fetch(`${API_BASE}/public/room/${roomId}/end`, {
      method: 'POST',
      headers: chatToken ? { Authorization: `Bearer ${chatToken}` } : {},
    }).then(r => ({ ok: r.ok, status: r.status })),

  // Renovacao silenciosa de token pra consulta que passa de 1h. Retorna
  // { token, chatToken, expiresInSeconds } ou { error }. Cliente deve
  // chamar ~5min antes do atual vencer.
  refreshToken: async (roomId, chatToken) => {
    const res = await fetch(`${API_BASE}/public/room/${roomId}/refresh-token`, {
      method: 'POST',
      headers: chatToken ? { Authorization: `Bearer ${chatToken}` } : {},
    });
    if (!res.ok) return { ok: false, status: res.status };
    const data = await res.json().catch(() => null);
    return { ok: true, ...data };
  },

  // Persiste preferencia de fundo do medico (so host). Mandado no toggle do
  // botao Fundo pra a proxima chamada ja entrar no estado que o medico escolheu.
  setBackgroundPreference: (roomId, chatToken, enabled) =>
    fetch(`${API_BASE}/public/room/${roomId}/background-preference`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(chatToken ? { Authorization: `Bearer ${chatToken}` } : {}),
      },
      body: JSON.stringify({ enabled }),
    }).then(r => ({ ok: r.ok, status: r.status })),

  uploadAttachment: (roomId, chatToken, file, senderName, senderRole) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('senderName', senderName);
    fd.append('senderRole', senderRole);
    return fetch(`${API_BASE}/rooms/${roomId}/chat/upload`, {
      method: 'POST',
      headers: chatToken ? { Authorization: `Bearer ${chatToken}` } : {},
      body: fd,
    }).then(r => r.json());
  },
};

// ==========================================
// API Admin (JWT Bearer obrigatorio)
// ==========================================
export const adminApi = {
  getStats: () =>
    adminJson(`${API_BASE}/admin/stats`),

  listClients: () =>
    adminJson(`${API_BASE}/admin/clients`),

  createClient: (data) =>
    adminJson(`${API_BASE}/admin/clients`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }),

  updateClient: (id, data) =>
    adminJson(`${API_BASE}/admin/clients/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }),

  getClient: (id) =>
    adminJson(`${API_BASE}/admin/clients/${id}`),

  // ---- Nodes (servidores LiveKit) ----
  listNodes: () =>
    adminJson(`${API_BASE}/admin/nodes`),

  createNode: (data) =>
    adminJson(`${API_BASE}/admin/nodes`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }),

  updateNode: (id, data) =>
    adminJson(`${API_BASE}/admin/nodes/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }),

  deleteNode: (id) =>
    adminJson(`${API_BASE}/admin/nodes/${id}`, { method: 'DELETE' }),

  // ---- Active rooms (todos os clientes, todos os nos) ----
  listActiveRooms: () =>
    adminJson(`${API_BASE}/admin/rooms/active`),

  roomParticipants: (roomId) =>
    adminJson(`${API_BASE}/admin/rooms/${roomId}/participants`),

  roomNetwork: (roomId, minutes = 10, limit = 50) =>
    adminJson(`${API_BASE}/admin/rooms/${roomId}/network?minutes=${minutes}&limit=${limit}`),

  forceCloseRoom: (roomId) =>
    adminJson(`${API_BASE}/admin/rooms/${roomId}/force-close`, { method: 'POST' }),

  // ---- Templates de background (admin) ----
  listTemplates: (clientId) =>
    adminJson(`${API_BASE}/admin/templates${clientId ? `?clientId=${clientId}` : ''}`),

  // multipart upload - recebe FormData
  createTemplate: (formData) =>
    adminJson(`${API_BASE}/admin/templates`, {
      method: 'POST',
      body: formData, // NAO setar Content-Type, browser cuida
    }),

  updateTemplate: (id, data) =>
    adminJson(`${API_BASE}/admin/templates/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }),

  deleteTemplate: (id) =>
    adminJson(`${API_BASE}/admin/templates/${id}`, { method: 'DELETE' }),

  // ---- Diagnostico (eventos de erro/warn vindos do navegador) ----
  listDiagnostics: (filters = {}) => {
    const qs = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== null && v !== undefined && v !== '') qs.set(k, v);
    });
    const q = qs.toString();
    return adminJson(`${API_BASE}/admin/diagnostics${q ? `?${q}` : ''}`);
  },

  listDiagnosticsRooms: (hours = 24) =>
    adminJson(`${API_BASE}/admin/diagnostics/rooms?hours=${hours}`),

  // Resumo de uso de TURN (direct vs relay, breakdown por provider).
  // Alimenta o card "Uso de TURN" na aba Diagnostico.
  getTurnUsage: (hours = 24) =>
    adminJson(`${API_BASE}/admin/diagnostics/turn-usage?hours=${hours}`),

  // Agregacao de problemas de audio por sala. Retorna { total, resolved,
  // resolvedExplicit, unresolved, resolvedPct, rooms[] }. Alimenta os cards
  // "Audio c/ problema" e "Audio resolvido" no topo da aba Diagnostico.
  getAudioIssues: (hours = 1) =>
    adminJson(`${API_BASE}/admin/diagnostics/audio-issues?hours=${hours}`),
};

// ==========================================
// API de Doctors (precisa X-Api-Key do cliente)
// ==========================================
export const doctorsApi = {
  list: (apiKey) =>
    fetch(`${API_BASE}/doctors`, {
      headers: { 'X-Api-Key': apiKey },
    }).then(r => r.json()),

  sync: (apiKey, data) =>
    fetch(`${API_BASE}/doctors/sync`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Api-Key': apiKey },
      body: JSON.stringify(data),
    }).then(r => r.json()),

  // Admin liga/desliga a feature auto-Picture-in-Picture do medico. Quando
  // off, o <video> remoto nao recebe o atributo autoPictureInPicture e a
  // feature fica inativa pra este medico em todas as consultas futuras.
  setPipAuto: (apiKey, externalId, enabled) =>
    fetch(`${API_BASE}/doctors/${encodeURIComponent(externalId)}/pip-auto`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'X-Api-Key': apiKey },
      body: JSON.stringify({ enabled }),
    }).then(r => r.json()),
};

// ==========================================
// API com API Key (para playground/clientes)
// ==========================================
export const clientApi = {
  createRoom: (apiKey, data = {}) =>
    fetch(`${API_BASE}/rooms`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Api-Key': apiKey },
      body: JSON.stringify({
        displayName: data.displayName || 'Sala de teste',
        maxParticipants: data.maxParticipants || 2,
        isLocked: data.isLocked || false,
      }),
    }).then(r => r.json()),

  listRooms: (apiKey, status = 'active') =>
    fetch(`${API_BASE}/rooms?status=${status}`, {
      headers: { 'X-Api-Key': apiKey },
    }).then(r => r.json()),

  getRoom: (apiKey, roomId) =>
    fetch(`${API_BASE}/rooms/${roomId}`, {
      headers: { 'X-Api-Key': apiKey },
    }).then(r => r.json()),

  deleteRoom: (apiKey, roomId) =>
    fetch(`${API_BASE}/rooms/${roomId}`, {
      method: 'DELETE',
      headers: { 'X-Api-Key': apiKey },
    }).then(r => r.json()),

  joinRoom: (apiKey, roomId, displayName, role = 'host') =>
    fetch(`${API_BASE}/rooms/${roomId}/join`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Api-Key': apiKey },
      body: JSON.stringify({ displayName, role }),
    }).then(r => r.json()),
};
